import React, { FunctionComponent, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { Grid, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { IStore, useGetStores } from "../../../../hooks/stores";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyStoreDialog } from "../ModifyStoreDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";
import { StoreCard } from "../StoreCard";

export const StoresDesktop: FunctionComponent<IStoresDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onStoreSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [stores, resetStores, setStores, noMoreStores] = useGetStores(isPublic, setLoading);
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<IStore>();
  const [searchText, setSearchText] = useState("");
  const [contextStore, setContextStore] = useState<IStore>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const filteredStores = useMemo(() => {
    if (searchText == "") return stores;
    return stores?.filter(store => {
      return store.name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [stores, searchText]);
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredStores}
      emptyIconType={attributeIconMap[AttributeType.STORES]}
      emptyString={isPublic ? strings.empty_stores_public : strings.empty_stores}
      addItemString={strings.add_attribute}
      title={strings.attribute_stores_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={openAdd}
      refreshAction={resetStores}
      onClearValue={onClearValue}
    >
      {filteredStores?.map(store => (
        <Grid item key={store.uuid} xs={columnWidth}>
          <StoreCard
            store={store}
            selected={preselectedId === store.uuid}
            onClick={() => {
              if (onStoreSelected) {
                onStoreSelected(store);
              } else {
                openEdit(store);
              }
            }}
            onContextMenu={event => {
              setContextStore(store);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreStores) &&
        Array.from({ length: 40 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 60 }} className={styles.skeleton} />
          </Grid>
        ))}
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextStore);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.alert_modify}
        </MenuItem>
      </Menu>
      <ModifyStoreDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        onClose={closeModifyDialog}
        store={modifyDialogState.item}
        onNewItem={newStore => {
          const newStores = [...stores, newStore];
          newStores.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setStores(newStores);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={newStore => {
          const newStores = stores.map(store => (store.uuid == newStore.uuid ? newStore : store));
          newStores.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setStores(newStores);
          toast.success(strings.generic_update_success);
        }}
        onDeletedItem={deletedStore => {
          const newStores = stores.filter(store => store.uuid != deletedStore.uuid);
          setStores(newStores);
          toast.success(strings.generic_update_success);
        }}
      />
    </AttributeDesktop>
  );
};

export interface IStoresDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: number;
  preselectedId?: string;
  onStoreSelected?: (store: IStore) => void;
  onClearValue?: () => void;
}
